import React, { useEffect, useState } from 'react';
import {
  Drawer, Form, Input, Select, Button, Divider, Alert, message,
} from 'antd';
import { PlusOutlined, DeleteOutlined, HolderOutlined } from '@ant-design/icons';
import apiClient from '../../utils/axios';

const OPERATOR_OPTIONS = [
  { value: 'equals', label: 'equals' },
  { value: 'not_equals', label: 'not equals' },
  { value: 'is_null', label: 'is null' },
  { value: 'is_not_null', label: 'is not null' },
  { value: 'gt', label: '>' },
  { value: 'gte', label: '>=' },
  { value: 'lt', label: '<' },
  { value: 'lte', label: '<=' },
  { value: 'contains', label: 'contains' },
  { value: 'in', label: 'in (comma-separated)' },
];

const NO_VALUE_OPERATORS = new Set(['is_null', 'is_not_null']);

function slugify(str) {
  return str
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^[^a-z]+/, '')  // strip leading non-letter chars so key always starts with a letter
    .replace(/_+$/g, '');
}

function extractErrorMessage(err, fallback) {
  const detail = err?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(d => d.msg || JSON.stringify(d)).join('; ');
  return fallback;
}

export default function LifecycleEditor({ open, lifecycle, onClose, onSaved }) {
  const isEdit = !!lifecycle;

  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [keyManuallyEdited, setKeyManuallyEdited] = useState(false);

  // Canonical property definitions for the condition builder property dropdown
  const [canonicalProps, setCanonicalProps] = useState([]);

  // Stages are managed as local state (array of { _id, key, label, order, conditions[] })
  // _id is a stable client-side ID used as React key — never sent to the server
  const [stages, setStages] = useState([]);

  // Load canonical properties once on open
  useEffect(() => {
    if (!open) return;
    apiClient.get('/integrations/canonical-properties')
      .then(res => setCanonicalProps(res.data.definitions || []))
      .catch(() => {}); // non-fatal
  }, [open]);

  // Populate form when editing an existing lifecycle
  useEffect(() => {
    if (!open) return;
    if (lifecycle) {
      form.setFieldsValue({
        name: lifecycle.name,
        key: lifecycle.key,
        description: lifecycle.description || '',
      });
      const sorted = [...(lifecycle.stages || [])].sort((a, b) => a.order - b.order);
      setStages(sorted.map(s => ({
        _id: crypto.randomUUID(),
        key: s.key,
        label: s.label,
        order: s.order,
        conditions: (s.conditions || []).map(c => ({ ...c, _id: crypto.randomUUID() })),
      })));
      setKeyManuallyEdited(true); // key is readonly after create
    } else {
      form.resetFields();
      setStages([]);
      setKeyManuallyEdited(false);
    }
    setError(null);
  }, [open, lifecycle, form]);

  const handleNameChange = (e) => {
    if (!isEdit && !keyManuallyEdited) {
      form.setFieldValue('key', slugify(e.target.value));
    }
  };

  // ── Stage management ──────────────────────────────────────────────────────

  const addStage = () => {
    setStages(prev => [
      ...prev,
      { _id: crypto.randomUUID(), key: '', label: '', order: prev.length, conditions: [] },
    ]);
  };

  const removeStage = (index) => {
    setStages(prev => {
      const next = prev.filter((_, i) => i !== index);
      return next.map((s, i) => ({ ...s, order: i }));
    });
  };

  const updateStageField = (index, field, value) => {
    setStages(prev => prev.map((s, i) => {
      if (i !== index) return s;
      const updated = { ...s, [field]: value };
      // Auto-generate stage key from label when the stage doesn't already have a server-assigned key
      if (field === 'label' && !s.key) {
        updated.key = slugify(value);
      }
      return updated;
    }));
  };

  // ── Condition management ──────────────────────────────────────────────────

  const addCondition = (stageIndex) => {
    setStages(prev => prev.map((s, i) => {
      if (i !== stageIndex) return s;
      return {
        ...s,
        conditions: [
          ...s.conditions,
          { _id: crypto.randomUUID(), property_key: '', operator: 'equals', value: '' },
        ],
      };
    }));
  };

  const removeCondition = (stageIndex, condIndex) => {
    setStages(prev => prev.map((s, i) => {
      if (i !== stageIndex) return s;
      return { ...s, conditions: s.conditions.filter((_, ci) => ci !== condIndex) };
    }));
  };

  const updateCondition = (stageIndex, condIndex, field, value) => {
    setStages(prev => prev.map((s, i) => {
      if (i !== stageIndex) return s;
      return {
        ...s,
        conditions: s.conditions.map((c, ci) => {
          if (ci !== condIndex) return c;
          const updated = { ...c, [field]: value };
          // Clear value when switching to a no-value operator
          if (field === 'operator' && NO_VALUE_OPERATORS.has(value)) {
            delete updated.value;
          }
          return updated;
        }),
      };
    }));
  };

  // ── Save ──────────────────────────────────────────────────────────────────

  const handleSave = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch {
      return; // form validation failed, antd shows inline errors
    }

    // Validate stages: each stage must have a label that produces a valid key
    const invalidStage = stages.some(s => !s.key && !slugify(s.label));
    if (invalidStage) {
      setError('Each stage must have a label that starts with a letter.');
      return;
    }

    // Validate stages: each condition must have a property and (for non-null operators) a value
    const invalidCondition = stages.some(s =>
      s.conditions.some(c =>
        !c.property_key || (!NO_VALUE_OPERATORS.has(c.operator) && !String(c.value ?? '').trim())
      )
    );
    if (invalidCondition) {
      setError('All conditions must have a property and value set.');
      return;
    }

    setError(null);
    setSaving(true);
    try {
      const payload = {
        name: values.name,
        key: values.key,
        description: values.description || '',
        stages: stages.map((s, i) => ({
          key: s.key || slugify(s.label),
          label: s.label,
          order: i,
          conditions: s.conditions.map(c => {
            const cond = { property_key: c.property_key, operator: c.operator };
            if (!NO_VALUE_OPERATORS.has(c.operator)) {
              cond.value = c.value;
            }
            return cond;
          }),
        })),
      };

      if (isEdit) {
        await apiClient.patch(`/lifecycles/${lifecycle.key}`, payload);
        message.success('Lifecycle updated');
      } else {
        await apiClient.post('/lifecycles', payload);
        message.success('Lifecycle created');
      }

      onSaved();
      onClose();
    } catch (err) {
      setError(extractErrorMessage(err, isEdit ? 'Failed to update lifecycle' : 'Failed to create lifecycle'));
    } finally {
      setSaving(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────

  const propertyOptions = canonicalProps.map(p => ({
    value: p.key,
    label: `${p.label} (${p.key})`,
  }));

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={isEdit ? `Edit: ${lifecycle?.name}` : 'New Lifecycle'}
      width={560}
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="primary" onClick={handleSave} loading={saving}>
            {isEdit ? 'Save changes' : 'Create lifecycle'}
          </Button>
        </div>
      }
    >
      {error && <Alert type="error" message={error} style={{ marginBottom: 16 }} />}

      <Form form={form} layout="vertical">
        <Form.Item
          name="name"
          label="Name"
          rules={[{ required: true, message: 'Name is required' }]}
        >
          <Input placeholder="e.g. Onboarding" onChange={handleNameChange} />
        </Form.Item>

        <Form.Item
          name="key"
          label="Key"
          tooltip={isEdit ? 'Key cannot be changed after creation' : 'Auto-generated from name, URL-safe'}
          rules={[
            { required: true, message: 'Key is required' },
            { pattern: /^[a-z][a-z0-9_]*$/, message: 'Lowercase letters, numbers and underscores only' },
          ]}
        >
          <Input
            placeholder="e.g. onboarding"
            disabled={isEdit}
            style={isEdit ? { fontFamily: 'monospace' } : undefined}
            onChange={() => { if (!isEdit) setKeyManuallyEdited(true); }}
          />
        </Form.Item>

        <Form.Item name="description" label="Description">
          <Input.TextArea
            placeholder="Briefly describe this lifecycle…"
            rows={2}
            autoSize={{ minRows: 2, maxRows: 4 }}
          />
        </Form.Item>
      </Form>

      <Divider style={{ margin: '8px 0 16px' }} />

      {/* Stages */}
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
        <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>Stages</span>
        <Button
          size="small"
          type="dashed"
          icon={<PlusOutlined />}
          style={{ marginLeft: 'auto' }}
          onClick={addStage}
        >
          Add stage
        </Button>
      </div>

      {stages.length === 0 && (
        <p style={{ color: 'var(--text-muted)', fontSize: 13, fontStyle: 'italic' }}>
          No stages yet. Add at least one stage.
        </p>
      )}

      {stages.map((stage, stageIndex) => (
        <div
          key={stage._id}
          style={{
            border: '1px solid var(--border-color)',
            borderRadius: 8,
            padding: '12px 14px',
            marginBottom: 12,
            background: 'var(--bg-secondary)',
          }}
        >
          {/* Stage header */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <HolderOutlined style={{ color: 'var(--text-muted)', cursor: 'grab' }} />
            <span style={{ color: 'var(--text-muted)', fontSize: 12, minWidth: 20 }}>
              {stageIndex + 1}.
            </span>
            <Input
              placeholder="Stage label (e.g. Sign Up)"
              value={stage.label}
              onChange={e => updateStageField(stageIndex, 'label', e.target.value)}
              style={{ flex: 1 }}
              size="small"
            />
            <Button
              size="small"
              danger
              type="text"
              icon={<DeleteOutlined />}
              onClick={() => removeStage(stageIndex)}
            />
          </div>

          {/* Stage key (readonly after create) */}
          {stage.key && (
            <div style={{ marginBottom: 8 }}>
              <span style={{
                fontFamily: 'monospace', fontSize: 11,
                color: 'var(--text-muted)', marginLeft: 36,
              }}>
                key: {stage.key}
              </span>
            </div>
          )}

          {/* Conditions */}
          {stage.conditions.length > 0 && (
            <div style={{ marginLeft: 36, marginBottom: 8 }}>
              <span style={{ fontSize: 11, color: 'var(--text-secondary)', fontWeight: 500 }}>
                Conditions (all must be true):
              </span>
              {stage.conditions.map((cond, condIndex) => (
                <div
                  key={cond._id}
                  style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 6 }}
                >
                  <Select
                    size="small"
                    placeholder="Property"
                    value={cond.property_key || undefined}
                    onChange={v => updateCondition(stageIndex, condIndex, 'property_key', v)}
                    options={propertyOptions}
                    showSearch
                    style={{ width: 150 }}
                    filterOption={(input, option) =>
                      (option?.label || '').toLowerCase().includes(input.toLowerCase())
                    }
                  />
                  <Select
                    size="small"
                    value={cond.operator}
                    onChange={v => updateCondition(stageIndex, condIndex, 'operator', v)}
                    options={OPERATOR_OPTIONS}
                    style={{ width: 130 }}
                  />
                  {!NO_VALUE_OPERATORS.has(cond.operator) && (
                    <Input
                      size="small"
                      placeholder="value"
                      value={cond.value ?? ''}
                      onChange={e => updateCondition(stageIndex, condIndex, 'value', e.target.value)}
                      style={{ flex: 1, minWidth: 80 }}
                    />
                  )}
                  <Button
                    size="small"
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => removeCondition(stageIndex, condIndex)}
                  />
                </div>
              ))}
            </div>
          )}

          <Button
            size="small"
            type="dashed"
            icon={<PlusOutlined />}
            style={{ marginLeft: 36, marginTop: 6 }}
            onClick={() => addCondition(stageIndex)}
          >
            Add condition
          </Button>
        </div>
      ))}
    </Drawer>
  );
}
